import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { defineMessages } from 'react-intl';
import { all, call, select } from 'redux-saga/effects';
import * as logger from 'universal-logger';
import { deepExtract } from 'helpers/deep';
import { isObject } from 'helpers/verify';
import { selectCountry } from 'redux/modules/locale';
import { cmsLegalContent } from 'redux/saga/entities/bound';
import { getMsa } from 'containers/LocationModal/location.saga';

export const messages = defineMessages({
  accountDisclaimer: {
    id: 'consent-default.account-disclaimer',
    defaultMessage: 'By signing up you agree to our {linkTerms} and verify you have read the {linkPrivacy}.',
  },
  facebookDisclaimer: {
    id: 'consent-default.facebook-disclaimer',
    defaultMessage:
      'If you sign up through Facebook, you agree to the {linkTerms} and {linkPrivacy}. If you do not want to receive ClassPass marketing communications, sign up with your email and leave the box unchecked.',
  },
  marketingDisclaimer: {
    id: 'consent-default.marketing-disclaimer',
    defaultMessage: 'Yes, please send me ClassPass emails with news, offers and other info.',
  },
  searchableDisclaimer: {
    id: 'consent-default.searchable-disclaimer',
    defaultMessage:
      'I understand my name will be searchable and that my name, city and photo may be visible to ClassPass members. I can change my privacy settings at any time.',
  },
});

const consentPath = countryCode => (countryCode ? `consent-account-creation-${countryCode}` : '');

export function logError(error) {
  const errorObj = isObject(error) ? error : { message: error };
  if (errorObj.httpStatus !== 404) {
    logger.error(errorObj.message);
  }
}

export function* loadAccountCreationConsentForDetectedLocation(client) {
  try {
    // Select country from geolocation
    const countryCode = yield select(selectCountry);
    return countryCode && (yield call(fetchEntityIfNeeded, cmsLegalContent, [client, consentPath(countryCode)]));
  } catch (error) {
    yield call(logError, error);
  }
}

export function* loadAccountCreationConsentForSelectedLocation(client) {
  try {
    // This will have no side effects as long as plan selection occurs
    // prior to the rest of the funnel
    const msa = yield call(getMsa, client);
    const countryCode = deepExtract('country.code', msa) || deepExtract('country_code', msa);
    return countryCode && (yield call(fetchEntityIfNeeded, cmsLegalContent, [client, consentPath(countryCode)]));
  } catch (error) {
    yield call(logError, error);
  }
}

export function loadAccountCreationConsentDefaults() {
  return {
    accountDisclaimer: messages.accountDisclaimer,
    facebookDisclaimer: messages.facebookDisclaimer,
    marketingDisclaimer: messages.marketingDisclaimer,
    marketing: false,
    searchableDisclaimer: messages.searchableDisclaimer,
    searchable: false,
    terms: messages.accountDisclaimer,
  };
}

export function* loadAccountCreationConsent(client) {
  // The user's current location legally dictates our consent requirements.
  // As a backup, we trust the user to self-report.  In case of a CMS outage,
  // we use the most widely applicable, privacy-sensitive defaults.
  const consent =
    (yield call(loadAccountCreationConsentForDetectedLocation, client)) ||
    (yield call(loadAccountCreationConsentForSelectedLocation, client)) ||
    (yield all([call(loadAccountCreationConsentDefaults), call(logger.warn, 'Falling back to default consents')]))[0];
  return {
    // Actual terms depends on signup form context (facebook/email/etc)
    // NOTE: don't override the disclaimer if the server starts returning one
    accountDisclaimer: consent.terms,
    ...consent,
    marketingOptInDisclaimer: consent.marketingDisclaimer,
    marketingOptInDefault: consent.marketing,
    marketingOptOutDisclaimer: consent.marketingOptOutDisclaimer,
    marketingOptOutDefault: consent.marketingOptOut,
    searchableDefault: consent.searchable,
  };
}

function transformActionCheckbox(defaultState) {
  return {
    form_type: 'checkbox',
    form_default: defaultState ? 'checked' : 'unchecked',
  };
}

function transformActionSubmit() {
  return { form_type: 'submit' };
}

// NOTE: Recording consent is a bit tricky.  Ideally, we only want to record
// decisions and would like to abstain from recording implied permissions if
// possbile (times when we assert consent through the terms).  Unfortunately,
// our system currently doesn't know how to distinguish between feature toggles
// and consent. Thus, we're in 1 of 2 cases:
// 1. The permission is covered by the terms language: the user consented by clicking submit
// 2. The permission has its own disclaimer: the user may consent through a toggle.
//    If so, then we need to know the toggle language and its default state as well.
export function transformConsentForApi({
  marketing,
  marketingDefault,
  marketingDisclaimer,
  searchable,
  searchableDefault,
  searchableDisclaimer,
  terms,
} = {}) {
  return [
    {
      action: transformActionSubmit(),
      consent_type: ['terms_of_use', 'privacy_policy'],
      is_granted: true,
      legal_copy: terms,
    },
    marketingDisclaimer
      ? {
          action: transformActionCheckbox(marketingDefault),
          consent_type: ['marketing_emails'],
          is_granted: !!marketing,
          legal_copy: marketingDisclaimer,
        }
      : {
          action: transformActionSubmit(),
          consent_type: ['marketing_emails'],
          is_granted: true,
          legal_copy: terms,
        },
    searchableDisclaimer
      ? {
          action: transformActionCheckbox(searchableDefault),
          consent_type: ['social_search'],
          is_granted: !!searchable,
          legal_copy: searchableDisclaimer,
        }
      : {
          action: transformActionSubmit(),
          consent_type: ['social_search'],
          is_granted: true,
          legal_copy: terms,
        },
  ];
}
